import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  TextField
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { ArrowDownIcon } from '@components/icons/svg-icon.tsx';
import { LanguageEnum } from '@models/language.model';
import { AppAutocompleteV2Props } from '@models/app-autocomplete.model.ts';
import { CrudActionEnum } from '@models/global.model.ts';
import AppImage from '@components/app-image/app-image.tsx';

const AppAutocompleteV2: React.FC<AppAutocompleteV2Props<any>> = ({
  name,
  control,
  label,
  language,
  options,
  withChip = false,
  multiple = false,
  disabled,
  validationProps,
  selectedList,
  placeholder,
  clearMessage = 'clear_selection',
  addItemConfig,
  renderOptionBadge,
  loading = false,
  onOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<any[]>([]);
  const [optionState, setOptionState] = useState(options);
  const [inputValue, setInputValue] = useState('');
  const addingInProgressRef = useRef(false);

  const {
    errors,
    errorMessages,
    validationRules = {},
    getErrorMessage
  } = validationProps || {};
  const action = addItemConfig?.action;
  const callbackFn = addItemConfig?.callback;

  useEffect(() => {
    setOptionState(options);
    setSelectedValues((prev) =>
      prev.map((sel) => {
        const selTitle = language ? sel.title?.[language] : sel.title;
        const match = options.find((opt: any) => {
          const optTitle = language ? opt.title?.[language] : opt.title;
          return optTitle?.toLowerCase() === selTitle?.toLowerCase();
        });
        return match || sel;
      })
    );
  }, [options]);

  useEffect(() => {
    if (selectedList?.length) {
      const matchedValues = selectedList.map((selected: any) => {
        return (
          optionState.find((option) => {
            const selectedTitle = language
              ? selected?.title[language]?.toLowerCase()
              : selected?.title?.toLowerCase();
            const optionTitle = language
              ? option?.title[language]?.toLowerCase()
              : option?.title?.toLowerCase();
            return selectedTitle === optionTitle;
          }) || selected
        );
      });
      setSelectedValues(matchedValues);
    } else {
      setSelectedValues([]);
    }
  }, [selectedList]);

  const handleAddNewItem = (value: string, field: any) => {
    if (addingInProgressRef.current) return;
    addingInProgressRef.current = true;
    setTimeout(() => { addingInProgressRef.current = false; }, 0);

    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    if (action === CrudActionEnum.Add) {
      const exists = optionState.some((opt: any) => {
        const title = language ? opt.title[language] : opt.title;
        return title?.toLowerCase() === trimmedValue.toLowerCase();
      });
      if (!exists) {
        const newOption = { title: trimmedValue, id: Date.now() };
        const updatedOptions = [...optionState, newOption];
        const updatedSelectedValues = [...selectedValues, newOption];
        setOptionState(updatedOptions);
        setSelectedValues(updatedSelectedValues);
        field.onChange(multiple ? updatedSelectedValues : newOption);

        if (callbackFn) {
          callbackFn({ title: trimmedValue });
        }
      }
    } else if (action === CrudActionEnum.AddWithDialog || callbackFn) {
      // AddWithDialog - только вызывает callback, не добавляет в список
      if (callbackFn) {
        callbackFn({ title: trimmedValue });
      }
    }

    setInputValue('');
  };

  return (
    <FormControl
      error={!!errors?.[name]}
      sx={{ minWidth: 300, width: '100%' }}
      disabled={disabled}
    >
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field }) => {
          const handleAddOption = (item: any) => {
            if (
              typeof item?.title === 'string' &&
              item.title.startsWith('__add__')
            ) {
              const val = item.title.replace('__add__', '');
              handleAddNewItem(val, field);
              return true;
            }
            return false;
          };

          const onChangeHandler = (_: any, newValue: any) => {
            if (
              !newValue ||
              (Array.isArray(newValue) && newValue.length === 0)
            ) {
              setSelectedValues([]);
              field.onChange(multiple ? [] : null);
              return;
            }

            if (multiple) {
              const addIndex = newValue.findIndex(
                (v: any) =>
                  typeof v.title === 'string' && v.title.startsWith('__add__')
              );
              if (addIndex !== -1) {
                handleAddOption(newValue[addIndex]);
                return;
              }
              setSelectedValues(newValue);
              field.onChange(newValue);
            } else {
              if (!handleAddOption(newValue)) {
                setSelectedValues([newValue]);
                field.onChange(newValue);
              }
            }
          };

          const displayOptions =
            inputValue && callbackFn
              ? [
                  ...optionState,
                  { id: 'add-new', title: `__add__${inputValue}` }
                ]
              : optionState;

          return (
            <Autocomplete
              disabled={disabled}
              multiple={multiple}
              disableCloseOnSelect={multiple}
              onOpen={onOpen}
              onClose={onClose}
              popupIcon={<ArrowDownIcon sx={{ width: '18px !important' }} />}
              options={displayOptions}
              noOptionsText={
                callbackFn ? t('no_options_text') : t('no_results_found')
              }
              getOptionLabel={(option: any) => {
                if (
                  typeof option.title === 'string' &&
                  option.title?.startsWith('__add__')
                ) {
                  return `Add "${option.title.replace('__add__', '')}"`;
                }
                return language
                  ? option.title?.[language] ||
                      option.title?.[LanguageEnum.English]
                  : option.title || '';
              }}
              value={multiple ? selectedValues : selectedValues[0] || null}
              onChange={onChangeHandler}
              onInputChange={(_, val, reason) => {
                if (reason === 'input') setInputValue(val);
                if (reason === 'clear') setInputValue('');
              }}
              onBlur={() => {
                setInputValue('');
                field.onBlur();
              }}
              renderOption={(props, option: any) => {
                if (
                  typeof option.title === 'string' &&
                  option.title.startsWith('__add__')
                ) {
                  return (
                    <li
                      {...props}
                      key="add-new"
                      style={{ fontStyle: 'italic', cursor: 'pointer' }}
                    >
                      Add "{option.title.replace('__add__', '')}"
                    </li>
                  );
                }

                const isSelected = selectedValues.some((selected) => {
                  const selectedTitle = language
                    ? selected?.title[language]?.toLowerCase() ||
                      selected?.title[LanguageEnum.English]?.toLowerCase()
                    : selected?.title?.toLowerCase();
                  const optionTitle = language
                    ? option?.title[language]?.toLowerCase() ||
                      option?.title[LanguageEnum.English]?.toLowerCase()
                    : option?.title?.toLowerCase();
                  return selectedTitle === optionTitle;
                });

                return (
                  <li
                    {...props}
                    key={option?.id ?? option?.title}
                    data-selected={isSelected}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {withChip ? (
                      <Chip
                        icon={
                          option.icon ? (
                            <AppImage
                              variant="image"
                              filePath={option.icon}
                              imgClassName="!h-auto"
                              objectFit={'contain'}
                              alt={option?.title}
                              fallback="N/A"
                              disableModalClick
                            />
                          ) : undefined
                        }
                        label={
                          language
                            ? option.title[language] ||
                              option.title[LanguageEnum.English]
                            : option.title
                        }
                      />
                    ) : (
                      <span className="option-label">
                        {language
                          ? option.title[language] ||
                            option.title[LanguageEnum.English]
                          : option.title}
                      </span>
                    )}
                    {renderOptionBadge && renderOptionBadge(option)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  error={!!errors?.[name]}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && (
                            <InputAdornment position="start">
                              <CircularProgress size={16} />
                            </InputAdornment>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inputValue.trim()) {
                      e.preventDefault();
                      handleAddNewItem(inputValue, field);
                    }
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  sx: withChip
                    ? {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '2px',
                        padding: '0px'
                      }
                    : {}
                },
                clearIndicator: { title: t(`${clearMessage}`) }
              }}
            />
          );
        }}
      />
      {validationProps && (
        <FormHelperText>
          {errors?.[name] &&
            getErrorMessage?.({
              errors,
              fieldName: name,
              errorMessages,
              validationRules
            })}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default AppAutocompleteV2;
